import { Component, ElementRef, OnDestroy, OnInit, ViewChild, Inject, PLATFORM_ID, ChangeDetectorRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@environments/environment';
import { ValentineThemeService } from '@app/core/services/valentine-theme.service';

@Component({
  selector: 'app-valentine-particles',
  template: `
    <canvas #canvas class="valentine-particles-canvas" [style.display]="isVisible ? 'block' : 'none'"></canvas>
  `,
  styles: [`
    .valentine-particles-canvas {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      opacity: 0.8;
    }
  `]
})
export class ValentineParticlesComponent implements OnInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvas: ElementRef<HTMLCanvasElement>;
  private ctx: CanvasRenderingContext2D;
  private particles: Heart[] = [];
  private animationFrame: number;
  private isBrowser: boolean;
  isVisible = true;
  
  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private valentineService: ValentineThemeService,
    private cdr: ChangeDetectorRef
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }
  
  ngOnInit() {
    if (this.isBrowser) {
      this.initCanvas();
      this.createParticles();
      this.animate();
      window.addEventListener('resize', this.onResize.bind(this));

      this.valentineService.particlesVisible$.subscribe(visible => {
        this.isVisible = visible;
        this.cdr.detectChanges();
      });
    }
  }
  
  ngOnDestroy() {
    if (this.isBrowser) {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
      }
      window.removeEventListener('resize', this.onResize.bind(this));
    }
  }
  
  private onResize() {
    if (this.canvas && this.canvas.nativeElement) {
      this.canvas.nativeElement.width = window.innerWidth;
      this.canvas.nativeElement.height = window.innerHeight;
    }
  }
  
  private initCanvas() {
    const canvasEl = this.canvas.nativeElement;
    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;
    this.ctx = ctx;
    canvasEl.width = window.innerWidth;
    canvasEl.height = window.innerHeight;
  }
  
  private createParticles() {
    const count = (environment.valentine && environment.valentine.particleCount) || 30;
    // Reduce count on mobile for performance
    const actualCount = window.innerWidth < 768 ? Math.floor(count / 2) : count;
    
    for (let i = 0; i < actualCount; i++) {
      this.particles.push(new Heart(
        Math.random() * window.innerWidth,
        Math.random() * window.innerHeight
      ));
    }
  }
  
  private animate() {
    if (!this.ctx || !this.canvas) return;
    
    this.ctx.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
    
    this.particles.forEach(particle => {
      particle.update();
      particle.draw(this.ctx);
    });
    
    this.animationFrame = requestAnimationFrame(() => this.animate());
  }
}

class Heart {
  x: number;
  y: number;
  size: number;
  speedY: number;
  speedX: number;
  opacity: number;
  color: string;
  
  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
    this.size = Math.random() * 12 + 8; // 8-20px
    this.speedY = -Math.random() * 1.5 - 0.5; // Float upward
    this.speedX = (Math.random() - 0.5) * 0.8; // Gentle horizontal drift
    this.opacity = Math.random() * 0.5 + 0.2; // 0.2-0.7
    
    // Randomize slightly between pink and purple
    const colors = ['#ff006e', '#fb5607', '#8338ec', '#ff006e'];
    this.color = colors[Math.floor(Math.random() * colors.length)];
  }
  
  update() {
    this.y += this.speedY;
    this.x += this.speedX;
    
    // Reset if off-screen (top)
    if (this.y < -50) {
      this.y = window.innerHeight + 50;
      this.x = Math.random() * window.innerWidth;
    }
    // Wrap horizontal
    if (this.x > window.innerWidth + 50) this.x = -50;
    if (this.x < -50) this.x = window.innerWidth + 50;
  }
  
  draw(ctx: CanvasRenderingContext2D) {
    ctx.save();
    ctx.globalAlpha = this.opacity;
    ctx.fillStyle = this.color;
    // ctx.shadowBlur = 5;
    // ctx.shadowColor = this.color;
    
    // Draw heart shape
    ctx.beginPath();
    const topCurveHeight = this.size * 0.3;
    ctx.moveTo(this.x, this.y + topCurveHeight);
    
    // Left curve
    ctx.bezierCurveTo(
      this.x, this.y, 
      this.x - this.size / 2, this.y, 
      this.x - this.size / 2, this.y + topCurveHeight
    );
    
    // Bottom left to tip
    ctx.bezierCurveTo(
      this.x - this.size / 2, this.y + (this.size + topCurveHeight) / 2, 
      this.x, this.y + (this.size + topCurveHeight) / 2, 
      this.x, this.y + this.size
    );
    
    // Tip to bottom right
    ctx.bezierCurveTo(
      this.x, this.y + (this.size + topCurveHeight) / 2, 
      this.x + this.size / 2, this.y + (this.size + topCurveHeight) / 2, 
      this.x + this.size / 2, this.y + topCurveHeight
    );
    
    // Right curve
    ctx.bezierCurveTo(
      this.x + this.size / 2, this.y, 
      this.x, this.y, 
      this.x, this.y + topCurveHeight
    );
    
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
}
